import type { LucideIcon } from 'lucide-react';
import { ClipboardList, FileText, HandCoins, Layers, Stethoscope } from 'lucide-react';
import type { PlanFeature } from '@/types/clinic';

type Role = 'ADMIN' | 'PROFESSIONAL' | 'RECEPTIONIST';

export interface OnboardingTopic {
  id: string;
  title: string;
  description: string;
  /** Detalhes curtos exibidos em lista abaixo da descrição. */
  points?: string[];
  icon: LucideIcon;
  /** Rota do botão "Ir para…". */
  to?: string;
  toLabel?: string;
  /** Sem `roles`, todos os perfis veem. */
  roles?: Role[];
  /** Sem `feature`, não há gate de plano. */
  feature?: PlanFeature;
}

export const ONBOARDING_TOPICS: OnboardingTopic[] = [
  {
    id: 'evolutions',
    title: 'Evoluções dependem de procedimento e técnicas',
    description:
      'Para registrar uma evolução, o procedimento da consulta precisa ter técnicas cadastradas.',
    points: [
      'Cadastre o procedimento e, no card dele, abra "Técnicas" para criar o catálogo.',
      'Toda evolução exige uma consulta vinculada e ao menos uma técnica aplicada.',
      'O texto livre é opcional. Se faltar técnica, você também pode criá-la na hora, dentro da evolução.',
    ],
    icon: Stethoscope,
    to: '/procedures',
    toLabel: 'Ir para Procedimentos',
    roles: ['ADMIN', 'PROFESSIONAL'],
    feature: 'EVOLUTIONS',
  },
  {
    id: 'receive',
    title: 'Receber a consulta já lança no financeiro',
    description:
      'Nos detalhes do agendamento, o botão "Receber" marca o pagamento e cria a receita como paga no financeiro.',
    points: [
      'Não é preciso criar a fatura antes nem depois.',
      'Se já existir uma cobrança pendente vinculada, ela recebe a baixa em vez de duplicar.',
      'Consultas canceladas e de pacote não podem ser recebidas por aqui.',
    ],
    icon: HandCoins,
    to: '/agenda',
    toLabel: 'Ir para Agenda',
    feature: 'AGENDA',
  },
  {
    id: 'packages',
    title: 'Planos de tratamento',
    description:
      'Pacotes agrupam procedimentos com um número de sessões e controlam quantas já foram usadas.',
    points: [
      'Crie o pacote no perfil da paciente, na aba "Pacotes".',
      'Ao agendar uma consulta, vincule-a ao pacote para descontar a sessão.',
      'O pacote fica concluído quando todas as sessões são usadas.',
    ],
    icon: Layers,
    to: '/patients',
    toLabel: 'Ir para Pacientes',
    roles: ['ADMIN', 'PROFESSIONAL'],
    feature: 'TREATMENT_PACKAGES',
  },
  {
    id: 'anamnesis',
    title: 'Opções de anamnese',
    description: 'Existem duas fichas, e cada seção é opcional: preencha só o que fizer sentido.',
    points: [
      'Pélvica e Gestação: escolha ao criar a anamnese, na aba "Anamnese" da paciente.',
      'Rascunho salva sozinho a cada 30 segundos e pode ser excluído.',
      'Finalizar é irreversível: depois disso a ficha só pode ser editada, nunca excluída.',
      'As dicas "Investigar:" orientam a conversa e não entram no registro.',
    ],
    icon: ClipboardList,
    to: '/patients',
    toLabel: 'Ir para Pacientes',
    roles: ['ADMIN', 'PROFESSIONAL'],
    feature: 'ANAMNESIS',
  },
  {
    id: 'physical-exam',
    title: 'Exame físico',
    description:
      'Fica na aba "Exame Físico" da paciente, com 7 seções opcionais e as mesmas regras de rascunho e finalização da anamnese.',
    points: ['Você pode vincular o exame a uma consulta (um exame por consulta) e gerar o relatório em PDF.'],
    icon: FileText,
    to: '/patients',
    toLabel: 'Ir para Pacientes',
    roles: ['ADMIN', 'PROFESSIONAL'],
  },
];

export function visibleTopics(
  role: Role | undefined,
  hasFeature: (f: PlanFeature) => boolean,
): OnboardingTopic[] {
  return ONBOARDING_TOPICS.filter(
    (t) => (!t.roles || (!!role && t.roles.includes(role))) && (!t.feature || hasFeature(t.feature)),
  );
}
